import { useEffect, useState } from 'react';

import {
  Badge,
  ButtonBase,
  Fab,
  Icon,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  Box,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';
import Draggable from 'react-draggable';

import * as socketEvents from 'constants/socketEvents';
import { Sounds, useSound } from 'hooks/useSound';
import socket from 'services/socket';

const dc = new Date().getTime(); // data cache
const iframeSrc = `${process.env.REACT_APP_HOMEPAGE_CHAT_INTERNO_URL}?dc=${dc}`;

const useStyles = makeStyles(({ palette, spacing, zIndex }) => ({
  buttonContainer: {
    zIndex: zIndex.drawer + 1,
    position: 'absolute',
    left: spacing(1),
    bottom: spacing(1.5),
  },
  animateButtonContainer: {
    animation: '$shakePulse 2.0s infinite',
  },
  '@keyframes shakePulse': {
    '5%, 50%': { transform: 'scale(1)' },
    '10%': { transform: 'scale(0.8)' },
    '15%': { transform: 'scale(1.15)' },
    '20%': { transform: 'scale(1.15) rotate(-5deg)' },
    '25%': { transform: 'scale(1.15) rotate(5deg)' },
    '30%': { transform: 'scale(1.15) rotate(-3deg)' },
    '35%': { transform: 'scale(1.15) rotate(2deg)' },
    '40%': { transform: 'scale(1.15) rotate(0)' },
  },
  paper: {
    height: 'calc(100vh - 200px)',
    width: 400,
    maxWidth: 420,
    position: 'absolute',
    left: spacing(10),
    bottom: spacing(6),
    zIndex: zIndex.drawer + 1,
    paddingBottom: spacing(8),
    backgroundColor: palette.background.paper,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing(2),
  },
  contentLoading: {
    position: 'absolute',
    right: 0,
    left: 0,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    userSelect: 'none',
  },
}));

export default function ButtonChatInterno() {
  const classes = useStyles();
  const theme = useTheme();
  const { play } = useSound();
  const [open, setOpen] = useState(false);
  const [loadingFrame, setLoadingFrame] = useState(true);
  const [totalMsgsChatInterno, setTotalMsgsChatInterno] = useState(0);
  const [iframeKey, setIframeKey] = useState(0);

  const toggleOpen = () => {
    setOpen(!open);
    if (!loadingFrame) setLoadingFrame(true);
  };

  const onLoadFrame = () => {
    setLoadingFrame(false);
  };

  // Recarrega o iframe quando o tema for alterado
  useEffect(() => {
    setIframeKey(key => key + 1);
  }, [theme.palette.type]);

  useEffect(() => {
    const handler = (data: { total: number }) => {
      const { total } = data;
      // Emite o som ao chegar uma nova mensagem no chat interno
      if (total > totalMsgsChatInterno) {
        play(Sounds.NOVA_MENSAGEM_CHAT_INTERNO);
      }

      setTotalMsgsChatInterno(total);
    };

    socket.on(
      socketEvents.ON_UPDATE_MENSAGENS_NAO_LIDAS_IN_CHAT_INTERNO,
      handler,
    );

    return () => {
      socket.off(
        socketEvents.ON_UPDATE_MENSAGENS_NAO_LIDAS_IN_CHAT_INTERNO,
        handler,
      );
    };
  }, [play, totalMsgsChatInterno]);

  return (
    <>
      <Box
        className={clsx(classes.buttonContainer, {
          [classes.animateButtonContainer]: totalMsgsChatInterno > 0,
        })}
      >
        <Badge
          color="error"
          overlap="circular"
          badgeContent={totalMsgsChatInterno}
        >
          <Tooltip arrow placement="right" title="Chat interno">
            <Fab
              size="small"
              color="primary"
              variant="circular"
              onClick={toggleOpen}
            >
              <Icon
                style={{ width: '100%' }}
                className="fas fa-comments"
                fontSize="small"
              />
            </Fab>
          </Tooltip>
        </Badge>
      </Box>

      {open && (
        <Draggable
          defaultPosition={{ x: 0, y: 0 }}
          handle=".handle"
          bounds="body"
          axis="both"
        >
          <Paper elevation={10} className={classes.paper}>
            <Box
              component="header"
              style={{ cursor: 'move' }}
              className={clsx('handle', classes.header)}
            >
              <Typography style={{ fontSize: 14 }}>Chat Interno</Typography>
              <ButtonBase onClick={toggleOpen}>
                <CloseIcon style={{ width: 24, height: 24 }} />
              </ButtonBase>
            </Box>

            {loadingFrame && (
              <Box className={classes.contentLoading}>
                <CircularProgress color="inherit" />
              </Box>
            )}

            <iframe
              id="iframeChatInterno"
              key={iframeKey}
              src={iframeSrc}
              onLoad={onLoadFrame}
              loading="eager"
              title="Chat Interno"
              width="100%"
              height="100%"
              allowFullScreen
              frameBorder="0"
              seamless
            />
          </Paper>
        </Draggable>
      )}
    </>
  );
}
